import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Gamepad2,
  Star,
  Zap,
  Coins,
  Clock,
  Ticket,
} from "lucide-react";
import "./styles.css";
import { apiUrl } from "../../constants";
import {
  maskGroup,
  maskGroup2,
  mobileTextPackages,
  packageDiv,
  packageText,
  textofPackage,
} from "../../assets";

import coin from "../../assets/coin_only.png";
import Bg from "./images/top.png";

const Packages = () => {
  const [regularPackages, setRegularPackages] = useState([]);
  const [unlimitedPackages, setUnlimitedPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("regular");
  const [currentPage, setCurrentPage] = useState(0);
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const navigate = useNavigate();
  const autoSlideInterval = useRef(null);

  const itemsPerPage = 3;

  const fetchData = async () => {
    setLoading(true);

    const fetchRegularPackages = async () => {
      try {
        const regularResponse = await axios.get(`${apiUrl}/package/`);
        const sortedRegularPackages = sortPackagesByPrice(
          regularResponse.data.packages || []
        );
        setRegularPackages(sortedRegularPackages);
        console.log("Regular packages loaded:", sortedRegularPackages.length);
      } catch (error) {
        console.error("Error fetching regular packages:", error);
        setRegularPackages([]);
      }
    };

    const fetchUnlimitedPackages = async () => {
      try {
        const unlimitedResponse = await axios.get(
          `${apiUrl}/unlimitedPackage/packages`
        );
        const sortedUnlimitedPackages = [
          ...(unlimitedResponse.data.data || []),
        ].sort((a, b) => a.totalCost - b.totalCost);
        setUnlimitedPackages(sortedUnlimitedPackages);
        console.log(
          "Unlimited packages loaded:",
          sortedUnlimitedPackages.length
        );
      } catch (error) {
        console.error("Error fetching unlimited packages:", error);
        setUnlimitedPackages([]);
      }
    };

    await Promise.allSettled([
      fetchRegularPackages(),
      fetchUnlimitedPackages(),
    ]);

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    window.scrollTo(0, 0);
  }, []);

  const currentPackages =
    activeTab === "regular" ? regularPackages : unlimitedPackages;
  const totalPages = Math.ceil(currentPackages.length / itemsPerPage);

  // Auto-slide functionality with 6-second interval
  useEffect(() => {
    // Clear any existing interval
    if (autoSlideInterval.current) {
      clearInterval(autoSlideInterval.current);
    }

    // Only start auto-slide if there are multiple pages
    if (currentPackages.length > itemsPerPage && totalPages > 1) {
      autoSlideInterval.current = setInterval(() => {
        setCurrentPage((prev) => (prev + 1) % totalPages);
      }, 6000); // 6 seconds
    }

    return () => {
      if (autoSlideInterval.current) {
        clearInterval(autoSlideInterval.current);
      }
    };
  }, [currentPackages.length, totalPages, activeTab]);

  // Reset page when switching tabs
  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const sortPackagesByPrice = (packages) => {
    return [...packages].sort((a, b) => a.price - b.price);
  };

  const getPackageRecommendation = (currentIndex) => {
    const nextPackage = regularPackages[currentIndex + 1];
    if (!nextPackage) return null;

    const currentPkg = regularPackages[currentIndex];
    const extraCoins = nextPackage.coins - currentPkg.coins;
    const extraCost = nextPackage.price - currentPkg.price;
    const betterValue =
      nextPackage.coins / nextPackage.price >
      currentPkg.coins / currentPkg.price;

    return {
      extraCoins,
      extraCost,
      betterValue,
      nextPackage,
    };
  };

  const getCurrentPageItems = () => {
    const startIndex = currentPage * itemsPerPage;
    return currentPackages.slice(startIndex, startIndex + itemsPerPage);
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
    // Clear auto-slide when user manually navigates
    if (autoSlideInterval.current) {
      clearInterval(autoSlideInterval.current);
    }
    // Restart auto-slide after manual navigation
    setTimeout(() => {
      if (currentPackages.length > itemsPerPage && totalPages > 1) {
        autoSlideInterval.current = setInterval(() => {
          setCurrentPage((prev) => (prev + 1) % totalPages);
        }, 6000);
      }
    }, 1000);
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
    // Clear auto-slide when user manually navigates
    if (autoSlideInterval.current) {
      clearInterval(autoSlideInterval.current);
    }
    // Restart auto-slide after manual navigation
    setTimeout(() => {
      if (currentPackages.length > itemsPerPage && totalPages > 1) {
        autoSlideInterval.current = setInterval(() => {
          setCurrentPage((prev) => (prev + 1) % totalPages);
        }, 6000);
      }
    }, 1000);
  };

  const goToPage = (pageIndex) => {
    setCurrentPage(pageIndex);
    // Clear auto-slide when user manually navigates
    if (autoSlideInterval.current) {
      clearInterval(autoSlideInterval.current);
    }
    // Restart auto-slide after manual navigation
    setTimeout(() => {
      if (currentPackages.length > itemsPerPage && totalPages > 1) {
        autoSlideInterval.current = setInterval(() => {
          setCurrentPage((prev) => (prev + 1) % totalPages);
        }, 6000);
      }
    }, 1000);
  };

  const renderRegularPackages = () => {
    const currentItems = getCurrentPageItems();

    return (
      <div className="relative">
        {/* Ticket-style grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-4 max-w-6xl mx-auto">
          {currentItems.map((pkg, index) => {
            const originalIndex = regularPackages.findIndex(
              (p) => p._id === pkg._id
            );
            const recommendation = getPackageRecommendation(originalIndex);
            const isPopular = originalIndex === 1;

            return (
              <div
                key={pkg._id}
                className={`ticket-card relative transform transition-all duration-300 hover:scale-105 hover:shadow-2xl min-h-[320px] w-full max-w-sm mx-auto ${
                  isPopular ? "ring-2 ring-yellow-400" : ""
                }`}
                style={{
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)",
                  borderRadius: "20px",
                  boxShadow:
                    "0 20px 60px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.8)",
                  border: "1px solid rgba(226,232,240,0.8)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Decorative perforated edge */}
                <div
                  className="absolute left-0 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-green-100 rounded-full"
                  style={{
                    marginLeft: "-12px",
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
                  }}
                />
                <div
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-green-100 rounded-full"
                  style={{
                    marginRight: "-12px",
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
                  }}
                />

                {/* Dotted line separator */}
                <div className="absolute left-6 right-6 top-1/2 transform -translate-y-1/2 border-t-2 border-dashed border-gray-300 opacity-50" />

                {isPopular && (
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-3 py-1 text-xs font-bold rounded-full z-10 shadow-lg">
                    <Star className="w-3 h-3 inline mr-1" />
                    POPULAR
                  </div>
                )}

                {/* Top section - Ticket header */}
                <div
                  className="bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 p-5 text-white relative"
                  style={{ borderRadius: "20px 20px 0 0" }}
                >
                  {/* Ticket number */}
                  <div className="absolute top-2 left-4 text-xs opacity-75 font-mono">
                    #{pkg._id.slice(-6).toUpperCase()}
                  </div>

                  <div className="mt-3">
                    <h3 className="text-lg font-bold mb-2">{pkg.name}</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <img src={coin} className="w-7 h-7 mr-2" />
                        <span className="text-2xl font-bold">
                          {pkg.coins.toLocaleString()}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-xs opacity-75 uppercase tracking-wide">
                          Price
                        </div>
                        <span className="text-xl font-bold">
                          {formatCurrency(pkg.price)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Decorative elements */}
                  <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                </div>

                {/* Bottom section - Ticket body */}
                <div className="p-4 bg-gradient-to-b from-gray-50 to-white">
                  {/* Value Rating */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700 font-semibold text-sm uppercase tracking-wide">
                        Value Rate:
                      </span>
                      <span className="font-bold text-green-600 text-base">
                        {(pkg.coins / pkg.price).toFixed(1)} coins/₹
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
                      <div
                        className="bg-gradient-to-r from-green-400 via-blue-500 to-purple-500 h-2 rounded-full transition-all duration-700 shadow-sm"
                        style={{
                          width: `${Math.min(
                            (pkg.coins / pkg.price) * 15,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Higher value = Better deal
                    </p>
                  </div>

                  {/* Recommendation */}
                  {recommendation && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 mb-4 border border-blue-200 shadow-sm">
                      <h4 className="font-bold text-gray-800 mb-1 flex items-center text-xs">
                        💡 Smart Upgrade Tip
                      </h4>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        Get{" "}
                        <span className="font-bold text-blue-600 bg-blue-100 px-1 py-0.5 rounded">
                          {recommendation.extraCoins.toLocaleString()} more
                          coins
                        </span>{" "}
                        for just{" "}
                        <span className="font-bold text-green-600 bg-green-100 px-1 py-0.5 rounded">
                          {formatCurrency(recommendation.extraCost)}
                        </span>
                      </p>
                      {recommendation.betterValue && (
                        <p className="text-xs text-purple-600 font-semibold mt-1 flex items-center">
                          🎯 Better value package!
                        </p>
                      )}
                    </div>
                  )}

                  {/* Purchase Button */}
                  <button
                    onClick={() =>
                      navigate("/book", {
                        state: {
                          selectedPackage: {
                            id: pkg._id,
                            type: "regular",
                          },
                        },
                      })
                    }
                    className="w-full bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 text-white py-3 px-4 rounded-xl font-bold hover:from-blue-600 hover:via-purple-700 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm tracking-wide uppercase"
                  >
                    🎫 Get This Package
                  </button>

                  {/* Ticket validation info */}
                  <div className="mt-3 text-center">
                    <p className="text-xs text-gray-500 font-mono">
                      Valid • Instant Access • No Expiry
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-12 space-x-6">
            <button
              onClick={goToPrevPage}
              className="p-4 rounded-full bg-white/95 backdrop-blur-sm text-gray-700 hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200 hover:border-gray-300"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <div className="flex space-x-3">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToPage(index)}
                  className={`w-5 h-5 rounded-full transition-all duration-300 ${
                    currentPage === index
                      ? "bg-blue-500 scale-125 shadow-lg"
                      : "bg-gray-300 hover:bg-gray-400 border border-gray-200"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={goToNextPage}
              className="p-4 rounded-full bg-white/95 backdrop-blur-sm text-gray-700 hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200 hover:border-gray-300"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* Auto-slide progress indicator */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <div className="text-sm text-gray-600 bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg border border-gray-200">
              Auto-advance in 6s • Page {currentPage + 1} of {totalPages}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderUnlimitedPackages = () => {
    const currentItems = getCurrentPageItems();

    return (
      <div className="relative">
        {/* Unlimited ticket-style grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-4 max-w-6xl mx-auto">
          {currentItems.map((pkg, index) => (
            <div
              key={pkg._id}
              className="ticket-card relative transform transition-all duration-300 hover:scale-105 hover:shadow-2xl min-h-[360px] w-full max-w-sm mx-auto"
              style={{
                background:
                  "linear-gradient(135deg, #ffffff 0%, #fef7ff 50%, #f3e8ff 100%)",
                borderRadius: "20px",
                boxShadow:
                  "0 20px 60px rgba(147,51,234,0.2), 0 8px 20px rgba(147,51,234,0.1), inset 0 1px 0 rgba(255,255,255,0.8)",
                border: "1px solid rgba(196,181,253,0.5)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative perforated edge */}
              <div
                className="absolute left-0 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-green-100 rounded-full"
                style={{
                  marginLeft: "-12px",
                  boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
                }}
              />
              <div
                className="absolute right-0 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-green-100 rounded-full"
                style={{
                  marginRight: "-12px",
                  boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
                }}
              />

              {/* Dotted line separator */}
              <div className="absolute left-6 right-6 top-1/2 transform -translate-y-1/2 border-t-2 border-dashed border-purple-300 opacity-50" />

              {/* Top section - Unlimited ticket header */}
              <div
                className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-500 p-5 text-white relative"
                style={{ borderRadius: "20px 20px 0 0" }}
              >
                {/* Ticket number */}
                <div className="absolute top-2 left-4 text-xs opacity-75 font-mono">
                  #{pkg._id.slice(-6).toUpperCase()}
                </div>

                {/* Unlimited badge */}
                <div className="absolute top-2 right-4 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
                  <span className="text-xs font-bold">∞ UNLIMITED</span>
                </div>

                <div className="mt-3">
                  <h3 className="text-lg font-bold mb-2">{pkg.packageName}</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Zap className="w-7 h-7 mr-2" />
                      <span className="text-2xl font-bold">∞</span>
                    </div>
                    <div className="text-right">
                      <div className="text-xs opacity-75 uppercase tracking-wide">
                        Total Cost
                      </div>
                      <span className="text-xl font-bold">
                        {formatCurrency(pkg.totalCost)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Decorative elements */}
                <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              </div>

              {/* Bottom section - Unlimited ticket body */}
              <div className="p-4 bg-gradient-to-b from-purple-50 to-white">
                {/* Available Days */}
                <div className="mb-4">
                  <div className="flex items-center mb-2">
                    <Calendar className="w-4 h-4 mr-2 text-purple-600" />
                    <span className="text-gray-700 font-semibold text-sm uppercase tracking-wide">
                      Available Days: {pkg.selectedDays.length}/7
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {pkg.selectedDays.slice(0, 4).map((day) => (
                      <span
                        key={day}
                        className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs capitalize border border-purple-200 shadow-sm font-medium"
                      >
                        {day.slice(0, 3)}
                      </span>
                    ))}
                    {pkg.selectedDays.length > 4 && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium shadow-sm">
                        +{pkg.selectedDays.length - 4} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Activities with Fixed Tooltip */}
                <div className="mb-4">
                  <div className="flex items-center mb-2">
                    <Gamepad2 className="w-4 h-4 mr-2 text-pink-600" />
                    <span className="text-gray-700 font-semibold text-sm uppercase tracking-wide">
                      {pkg.activities.length} Activities Included
                    </span>
                  </div>
                  <div
                    className="relative"
                    onMouseEnter={() => setHoveredTooltip(`tooltip-${index}`)}
                    onMouseLeave={() => setHoveredTooltip(null)}
                  >
                    <div className="bg-gradient-to-r from-gray-50 to-purple-50 rounded-lg p-3 text-center border border-purple-100 hover:from-purple-50 hover:to-pink-50 transition-all duration-200 cursor-pointer shadow-sm">
                      <Ticket className="w-4 h-4 mx-auto mb-1 text-purple-600" />
                      <span className="text-sm text-gray-700 font-semibold">
                        View All Activities
                      </span>
                    </div>

                    {/* Tooltip with pointer-events enabled */}
                    {hoveredTooltip === `tooltip-${index}` && (
                      <div
                        className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 z-50 pointer-events-auto"
                        onMouseEnter={() =>
                          setHoveredTooltip(`tooltip-${index}`)
                        }
                        onMouseLeave={() => setHoveredTooltip(null)}
                      >
                        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 w-72 max-h-48 overflow-y-auto">
                          <h4 className="font-bold text-gray-800 mb-3 text-center border-b pb-2">
                            Unlimited Access To:
                          </h4>
                          <div className="space-y-2">
                            {pkg.activities.map((activity, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded-lg"
                              >
                                <span className="text-gray-700 font-medium">
                                  {activity.name || `Activity ${idx + 1}`}
                                </span>
                                <span className="text-purple-600 font-bold text-lg">
                                  ∞
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Tooltip arrow */}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45 -mt-1.5"></div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Value Indicator */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700 font-semibold text-sm uppercase tracking-wide">
                      Package Type:
                    </span>
                    <span className="font-bold text-purple-600 text-sm bg-purple-100 px-2 py-1 rounded-full">
                      UNLIMITED
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
                    <div className="bg-gradient-to-r from-purple-400 via-pink-500 to-red-400 h-2 rounded-full w-full shadow-sm" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Day Pass • Unlimited usage • Best value
                  </p>
                </div>

                {/* Purchase Button */}
                <button
                  onClick={() =>
                    navigate("/book", {
                      state: {
                        selectedPackage: {
                          id: pkg._id,
                          type: "unlimited",
                        },
                      },
                    })
                  }
                  className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-red-500 text-white py-3 px-4 rounded-xl font-bold hover:from-purple-700 hover:via-pink-700 hover:to-red-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm tracking-wide uppercase"
                >
                  🎫 Get Unlimited Pass
                </button>

                {/* Ticket validation info */}
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-500 font-mono">
                    Premium • Unlimited Access • Day Access
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-12 space-x-6">
            <button
              onClick={goToPrevPage}
              className="p-4 rounded-full bg-white/95 backdrop-blur-sm text-gray-700 hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200 hover:border-gray-300"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <div className="flex space-x-3">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToPage(index)}
                  className={`w-5 h-5 rounded-full transition-all duration-300 ${
                    currentPage === index
                      ? "bg-purple-500 scale-125 shadow-lg"
                      : "bg-gray-300 hover:bg-gray-400 border border-gray-200"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={goToNextPage}
              className="p-4 rounded-full bg-white/95 backdrop-blur-sm text-gray-700 hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200 hover:border-gray-300"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* Auto-slide progress indicator */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <div className="text-sm text-gray-600 bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg border border-gray-200">
              Auto-advance in 6s • Page {currentPage + 1} of {totalPages}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="text-center">
 <div className="relative h-[35vh] sm:h-[40vh] md:h-[50vh] lg:h-[60vh] flex items-center justify-center overflow-hidden mb-8 md:mb-12 px-4">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-contain bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${Bg})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
          }}
        ></div>

        {/* Yellow/Green Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-400/60 via-blue-500/50 to-green-600/40"></div>

        {/* Heading */}
        <h1
          className="relative text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white tracking-wider z-10 text-center px-4 max-w-full break-words"
          style={{
            textShadow: "4px 4px 8px rgba(0,0,0,0.5), 0 0 20px rgba(0,0,0,0.3)",
            fontFamily: "Arial Black, sans-serif",
            letterSpacing: "0.05em",
            wordWrap: "break-word",
            hyphens: "auto",
          }}
        >
          Packages
        </h1>
      </div>

      <section className="section2 flex flex-col justify-center items-center mt-[-50px] max-sm:mt-[0px] bg-green-100  ">
        <div className=" inset-0 flex items-start justify-center w-full h-full pt-32 max-sm:pt-16">
          <div className="w-[95%] max-w-7xl mx-auto">
            {/* Tab Navigation */}
            <div className="flex justify-center mb-12 relative z-10">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-2 flex border border-white/30">
                <button
                  onClick={() => setActiveTab("regular")}
                  className={`px-8 py-4 rounded-xl font-bold transition-all duration-300 text-lg ${
                    activeTab === "regular"
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105"
                      : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <img src={coin} className="w-7 h-7 inline mr-3" />
                  Coins Packages
                </button>
                <button
                  onClick={() => setActiveTab("unlimited")}
                  className={`px-8 py-4 rounded-xl font-bold transition-all duration-300 text-lg ${
                    activeTab === "unlimited"
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg transform scale-105"
                      : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <Zap className="w-5 h-5 inline mr-2" />
                  Unlimited Packages
                </button>
              </div>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
              </div>
            ) : (
              /* Package Content */
              <div className="transition-all duration-500">
                {activeTab === "regular"
                  ? renderRegularPackages()
                  : renderUnlimitedPackages()}
              </div>
            )}

            {/* Empty State */}
            {!loading && currentPackages.length === 0 && (
              <div className="text-center py-16">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 max-w-md mx-auto shadow-xl border border-white/30">
                  <Clock className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-bold text-gray-600 mb-2">
                    No packages available
                  </h3>
                  <p className="text-gray-500">
                    {activeTab === "regular" ? "Regular" : "Unlimited"} packages
                    will appear here when available.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Packages;
